/**
 * Formatação pt-BR.
 *
 * Um princípio atravessa o arquivo: **ausência não vira zero.** No R, um valor
 * que não existia virava `NA` e depois `0` no gráfico, e ninguém conseguia
 * distinguir "não houve variação" de "não há observação anterior". Aqui `null`
 * sai como `—` em todo lugar.
 *
 * `NaN` e `Infinity` entram na mesma vala: o R devolve `-Inf` de `max()` sobre
 * conjunto vazio, e um `-Inf` formatado como número é pior do que um traço.
 */

const NADA = '—'

function ausente(valor: number | null | undefined): valor is null | undefined {
  return valor === null || valor === undefined || !Number.isFinite(valor)
}

const decimal = (casas: number) =>
  new Intl.NumberFormat('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas })

const inteiro = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 })

/**
 * Preço em R$/L com 4 casas.
 *
 * Quatro e não duas: combustível é cotado na quarta casa, e arredondar para
 * centavo apaga a diferença que o painel existe para mostrar. O banco guarda
 * cinco — a quinta é dado real do custo calculado, não ruído — mas exibir cinco
 * casas num eixo não ajuda ninguém a ler.
 */
export function preco(valor: number | null | undefined, casas = 4): string {
  if (ausente(valor)) return NADA
  return decimal(casas).format(valor)
}

/** Delta e defasagem: o sinal é a informação, então ele é sempre explícito. */
export function comSinal(valor: number | null | undefined, casas = 4): string {
  if (ausente(valor)) return NADA
  const texto = decimal(casas).format(Math.abs(valor))
  if (valor > 0) return `+${texto}`
  if (valor < 0) return `−${texto}`
  return texto
}

export function percentual(valor: number | null | undefined, casas = 2): string {
  if (ausente(valor)) return NADA
  return `${decimal(casas).format(valor)}%`
}

export function contagem(valor: number | null | undefined): string {
  if (ausente(valor)) return NADA
  return inteiro.format(valor)
}

/** `2026-01-02` → `02/01/2026`, sem passar por `Date` e sem fuso no caminho. */
export function data(iso: string | null | undefined): string {
  if (!iso) return NADA
  const partes = iso.slice(0, 10).split('-')
  if (partes.length !== 3) return iso
  const [ano, mes, dia] = partes
  return `${dia}/${mes}/${ano}`
}

/** Rótulo curto para eixo de tempo: `02/01`. */
export function dataCurta(iso: string | null | undefined): string {
  if (!iso) return NADA
  const partes = iso.slice(0, 10).split('-')
  if (partes.length !== 3) return iso
  return `${partes[2]}/${partes[1]}`
}

export function dataHora(iso: string | null | undefined): string {
  if (!iso) return NADA
  const instante = new Date(iso)
  if (Number.isNaN(instante.getTime())) return iso
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(instante)
}

/** `1234` → `1,2 mil`. Só para tiles; tabela usa o número inteiro. */
export function compacto(valor: number | null | undefined): string {
  if (ausente(valor)) return NADA
  if (Math.abs(valor) < 10_000) return inteiro.format(valor)
  return new Intl.NumberFormat('pt-BR', { notation: 'compact', maximumFractionDigits: 1 }).format(
    valor,
  )
}

/** `hoje()` e `mesesAtras()` alimentam o período padrão dos filtros. */
export function hoje(): string {
  return new Date().toISOString().slice(0, 10)
}

export function mesesAtras(meses: number): string {
  const d = new Date()
  d.setMonth(d.getMonth() - meses)
  return d.toISOString().slice(0, 10)
}
